import { MathUtils } from '@skand/three-js-renderer/bin/private/math';
import { Line3, Matrix4, Plane, Quaternion, Vector3, Vector4 } from 'three';
/**
 * Defines the behavior of a line path
 */
export class LinePathObject {
    constructor(renderer, origin, direction) {
        Object.defineProperty(this, "renderer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "origin", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "direction", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "currentPosition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "draggingPlane", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.origin = origin;
        this.direction = direction;
        this.currentPosition = origin.clone();
        this.draggingPlane = new Plane();
        this.renderer = renderer;
    }
    /**
     * Calculate the dragging plane on which the widget moves relative
     * to the camera
     */
    onGrab() {
        const camera = this.renderer.getWorld().getCamera();
        const cameraDirection = new Vector3(0, 0, -1);
        const cameraRotation = new Quaternion();
        camera.getRotation(cameraRotation);
        cameraDirection.applyQuaternion(cameraRotation);
        const draggingPlaneNormal = this.direction.clone().cross(cameraDirection);
        draggingPlaneNormal.crossVectors(this.direction, draggingPlaneNormal).normalize();
        this.draggingPlane.setFromNormalAndCoplanarPoint(draggingPlaneNormal, this.origin);
    }
    /**
     * Do nothing on release
     */
    onRelease() { }
    /**
     * Move the object along the line based on the 2D motion gesture
     */
    mapMotion(position, gesture) {
        const camera = this.renderer.getWorld().getCamera();
        const cameraPosition = new Vector3();
        camera.getPositionECEF(cameraPosition);
        const cameraRotation = new Quaternion();
        camera.getRotation(cameraRotation);
        // Get NDC on near plane for last cursor position
        const size = this.renderer.getSize();
        const prevRayDir = MathUtils.screenCoordsToNDC(gesture.prevPosition.x, gesture.prevPosition.y, size.x, size.y, new Vector4(0, 0, 0, 1));
        prevRayDir.y *= -1;
        // Get NDC on far plane for last cursor position
        const currRayDir = MathUtils.screenCoordsToNDC(gesture.position.x, gesture.position.y, size.x, size.y, new Vector4(0, 0, 0, 1));
        currRayDir.y *= -1;
        // Get direction to near plane by applying inverse projection and dividing by W
        const invProj = new Matrix4();
        camera.getPerspective().getInverseProjectionMatrix(invProj);
        prevRayDir.applyMatrix4(invProj);
        prevRayDir.divideScalar(prevRayDir.w);
        currRayDir.applyMatrix4(invProj);
        currRayDir.divideScalar(currRayDir.w);
        const length = position.length() * 2;
        const ray = new Line3(cameraPosition);
        // Find the intersection of these rays with the dragging plane
        ray.end.x = prevRayDir.x;
        ray.end.y = prevRayDir.y;
        ray.end.z = prevRayDir.z;
        ray.end.normalize().multiplyScalar(length).applyQuaternion(cameraRotation).add(cameraPosition);
        const prevIntersect = new Vector3();
        this.draggingPlane.intersectLine(ray, prevIntersect);
        ray.end.x = currRayDir.x;
        ray.end.y = currRayDir.y;
        ray.end.z = currRayDir.z;
        ray.end.normalize().multiplyScalar(length).applyQuaternion(cameraRotation).add(cameraPosition);
        const currIntersect = new Vector3();
        this.draggingPlane.intersectLine(ray, currIntersect);
        // The displacement between these intersection points
        // will be the projected displacement of the widget along the path
        const displacement = currIntersect.sub(prevIntersect).projectOnVector(this.direction);
        // Update internal position value and return
        this.currentPosition = position.add(displacement);
        return this.currentPosition;
    }
    /**
     * Get the target position for rendering
     */
    getWidgetPosition() {
        return this.currentPosition;
    }
    /**
     * The progress value is just how far along the widget is on the line
     */
    getProgress() {
        const disp = this.currentPosition.clone().sub(this.origin);
        const dot = disp.dot(this.direction);
        if (dot < 0) {
            return -disp.length();
        }
        else {
            return disp.length();
        }
    }
    /**
     * Set how far long the widget is on the line based on progress value
     */
    setFromProgress(progress) {
        this.currentPosition = this.direction.clone().multiplyScalar(progress).add(this.origin);
    }
}
